import { SocketProvider } from '../Socket/SocketProvider';
import { InteractionHandler } from './InteractionHandler';
import { AppSections } from '../State/AppSections';
import { resetQueryClient } from '../Query/QueryClient';
import { t } from '../../i18n';
import { releaseReservedWindow } from '../../Utils/DetachedWindowManager';
import { isPopupDebugEnabled } from '../../Utils/PopupDebug';
import { reportDetachedIssue } from '../../Utils/DetachedIssueLogger';
import { invalidateTournamentScheduleQueries } from '../../Tournament/TournamentSchedule';

export class ServerHandler {

    private connection: SocketProvider;
    private interactionHandler: InteractionHandler;
    private gameStartingClearTimers: Map<string, NodeJS.Timeout> = new Map();

    constructor(connection: SocketProvider, interactionHandler: InteractionHandler) {
        this.connection = connection;
        this.interactionHandler = interactionHandler;
    }

    public handleMessage(type?: string, event?: string, data?: any) {
        if (!type || !event) {
            console.error('Invalid message received:', { type, event, data });
            return;
        }
        switch (type) {
            case 'authentication':
                this.handleAuthenticationMessage(event, data);
                break;
            case 'app':
                this.handleAppMessage(event, data);
                break;
            case 'lobby':
                this.handleLobbyMessage(event, data);
                break;
            case 'room':
                this.handleRoomMessage(event, data);
                break;
            case 'player':
                this.handlePlayerMessage(event, data);
                break;
            case 'tournament':
                this.handleTournamentMessage(event, data);
                break;
            default:
                if (event != 'gameResults') {
                    console.error('Unknown message type:', type);
                }
        }
    }

    private handleAuthenticationMessage = (event: string, data: any) => {
        switch (event) {
            case 'loginSuccess':
                const wasAuthenticated = this.interactionHandler.isAuthenticated?.() === true;
                const existingSection = this.interactionHandler.getCurrentSection?.();
                const nextSection = (wasAuthenticated && existingSection && existingSection !== AppSections.LOGIN)
                    ? existingSection
                    : AppSections.LOBBY_HOME;
                const userData = {
                    ...data,
                    section: nextSection,
                    isProfileOpen: false,
                    singleViewRoomId: wasAuthenticated ? (this.interactionHandler.getSingleViewRoomId?.() ?? null) : null,
                    hasCredentialError: false,
                    hasFocedLogoutMaxClients: false
                };

                this.interactionHandler.setUserState(userData);
                this.interactionHandler.setConnected(true);

                if (data.pokerUserSettings) {
                    this.interactionHandler.userSettingsManager.loadSettings(data.pokerUserSettings, true);
                }

                this.interactionHandler.rejoinSubscriptionsAfterAuth?.();

                this.connection?.send('app', 'getUserConnectedCount', {});

                break;

            case 'loginFailed':
                if (data?.reason === 'login_conflict_expired') {
                    this.interactionHandler.handleLoginConflictExpired(data);
                    break;
                }
                this.handleLoginFailed();
                break;
            case 'tokenExpired':
                this.handleLoginFailed();
                break;

            case 'loginConflict':
                this.interactionHandler.handleLoginConflict(data);
                break;

            case 'loginTakeoverCancelled':
                this.interactionHandler.handleLoginTakeoverCancelled(data);
                break;

            case 'forceLogout':
                this.handleForceLogout();
                break;
        }
    }

    private handleLoginFailed = () => {
        this.connection?.clearHeartbeat();
        this.interactionHandler.resetState();
        localStorage.removeItem('sessionToken');
        void resetQueryClient();

        setTimeout(() => {
            this.interactionHandler.setUserLoginCredentialError(true);
        }, 100);
    }

    private handleForceLogout = () => {
        this.connection?.disconnect();
        this.interactionHandler.resetState();
        // Clear session token on force logout
        localStorage.removeItem('sessionToken');
        void resetQueryClient();
        setTimeout(() => {
            this.interactionHandler.setForcedLogoutMaxClients(true);
        }, 100);
        console.error(`Forced logout due to max clients reached`);
    }

    private handlePlayerMessage = (event: string, data: any) => {
        switch (event) {
            case 'avatarUpdate':
                this.interactionHandler.handleAvatarUpdate(data);
                break;
            case 'avatarUpdateFailed':
                this.interactionHandler.handleAvatarUpdateFailed(data);
                break;
            case 'walletBalanceUpdate':
                this.interactionHandler.updateWalletBalance(data);
                break;
            case 'ticketsUpdate': {
                const tickets = data?.tickets;
                this.interactionHandler.updateUserTickets(Array.isArray(tickets) ? tickets : []);
                break;
            }
            case 'updatePlayerConnectionStatus':
                // expects data: { userId, isDisconnected, roomId, tournamentId? }
                this.interactionHandler.updatePlayerConnectionStatus(data);
                break;
            case 'userSettingsUpdated':
                this.interactionHandler.handleUserSettingsUpdated(data);
                break;
            case 'userSettingsUpdateFailed':
                this.interactionHandler.handleUserSettingsUpdateFailed(data);
                break;
        }
    }

    /**
     * Handle lobby messages from the server
     * 
     * For joinSuccessful events:
     * - When lobbyData is present, it will be used to update the appropriate state
     * - For type='home', data goes to home lobby summary state
     * - For type='tournament', MTT list/modal data is ignored here and loaded via GraphQL polling
     * - For type='tournament_rooms' or 'normal', data goes to lobby state
     * 
     * This eliminates the need to call requestLobbyUpdate after joining a lobby
     */
    private handleLobbyMessage = (event: string, data: any) => {
        switch (event) {
            case 'lobbyUpdate':
                this.interactionHandler.updateLobbyState(data);
                break;
            case 'joinSuccessful':
                if (data.lobbyData) {
                    const lobbyData = data.lobbyData;
                    if (data.type === 'home') {
                        this.interactionHandler.updateLobbyState({
                            type: 'home',
                            ...lobbyData
                        });
                    } else if (data.type === 'tournament') {
                        break;
                    } else if (data.type === 'tournament_rooms') {
                        this.interactionHandler.updateLobbyState({
                            ...lobbyData
                        });
                    } else if (data.type === 'tournament_rankings') {
                        this.interactionHandler.updateLobbyTournamentRankingsState({
                            ...lobbyData
                        });
                    } else if (data.type === 'normal') {
                        this.interactionHandler.updateLobbyState({
                            type: 'normal',
                            ...lobbyData
                        });
                    }
                }
                break;
            case 'chatMessage':
                this.interactionHandler.addChatMessage(data.message, data.user);
                break;
            case 'chatHistory':
                this.interactionHandler.setChatHistory(data);
                break;
            default:
                if (event != 'leaveSuccessful') {
                    //console.warn(`Unhandled lobby event: ${event}`);
                }
        }
    }

    private handleRoomMessage = (event: string, data: any) => {
        switch (event) {
            case 'sitSuccessful':
            case 'sitSuccessNextRound':
                this.interactionHandler.handleSitOnRoomSuccessful(data);
                break;

            case 'sitFailed':
                this.handleSitFailed(data);
                break;

            case 'sitOptionsResponse':
                if (!data.gameState) {
                    data.gameState = {
                        isGameActive: false,
                        requiresChoice: false,
                        postOrWaitOptions: null
                    };
                }
                this.interactionHandler.handleSitOptionsResponse(data);
                break;
            case 'stateUpdate':
                this.interactionHandler.handleRoomStateUpdate(data);
                break;
            case 'joinSuccessful':
                this.interactionHandler.handleRoomJoinSuccessful(data);
                break;
            case 'leaveSuccessful':
                this.handleRoomLeaveSuccessful(data);
                break;
            case 'leaveFailed':
                this.handleRoomLeaveFailed(data);
                break;
            case 'joinFailed':
                this.handleRoomJoinFailed(data);
                break;
            case 'activityMessage':
                this.handleRoomActivityMessage(data);
                break;
            case 'sitFailedNoMoney':
                this.interactionHandler.handleSitOnRoomFailedNoMoney(data);
                break;
            case 'playerTurn':
                this.interactionHandler.handlePlayerTurnRequest(data);
                break;
            case 'autoActionUpdated':
                this.interactionHandler.handleAutoActionUpdated(data);
                break;
            case 'autoActionCancelled':
                this.interactionHandler.handleAutoActionCancelled(data);
                break;
            case 'autoActionRejected':
                this.interactionHandler.handleAutoActionRejected(data);
                break;
            case 'gameplayEvent':
                this.interactionHandler.handleGameplayEvent(data);
                break;
            case 'tableReaction':
                this.interactionHandler.handleTableReaction(data);
                break;
            case 'tableReactionRejected':
                this.interactionHandler.handleTableReactionRejected(data);
                break;
            case 'tableReactionSnapshot':
                break;
            case 'rejoinSuccessful':
                break;
            case 'rejoinFailed':
                this.interactionHandler.handleRejoinFailed(data);
                break;
            case 'postOrWaitOptions':
                this.interactionHandler.handlePostOrWaitOptions(data);
                break;
            case 'balanceWarning':
                this.interactionHandler.handleBalanceWarning(data);
                break;
            case 'showCardsOption':
                this.interactionHandler.handleShowCardsOption(data);
                break;
            case 'balanceOptionsResponse':
                this.handleBalanceOptionsResponse(data);
                break;
            case 'balanceUpdateFailed':
                this.interactionHandler.handleBalanceUpdateFailed(data);
                break;
            case 'gameStarting':
                this.handleGameStarting(data);
                break;
            case 'setGameId':
                this.interactionHandler.setGameId(data);
                break;
            case 'joinInClient': {
                try {
                    this.interactionHandler.joinRoomFromServerRequest(data);
                } catch (error) {
                    console.error('[ServerHandler] Error processing joinInClient event:', error, data);
                    // Show user-friendly error message
                    this.interactionHandler.handleServerMessage({
                        message: 'Failed to join tournament table. Please try refreshing the page.',
                        type: 'error'
                    });
                }
                break;
            }
            case 'tournamentEliminationStatus':
                this.interactionHandler.handleTournamentEliminationStatus?.(data);
                break;
            default:
                if (event != 'gameResults') {
                    //console.warn(`Unhandled room event: ${event}`);
                }
        }
    }

    private handleRoomLeaveSuccessful = (data: any) => {
        if(data.action === 'stand') {
            return;
        }
        if (data.action === 'leaveRoom' && data.deferred === true) {
            // Deferred leave: do not navigate away or remove room; stay in current view
            // Notify user that they'll leave after the hand ends
            // Route as room-scoped notification if roomId is available
            this.interactionHandler.handleServerMessage({
                message: 'You will leave the table after the current hand ends',
                type: 'SYSTEM_MESSAGE',
                scope: data.roomId ? 'room' : 'app',
                roomId: data.roomId
            });
            return;
        }
        this.interactionHandler.removeJoinedRoom(data.roomId);
        this.interactionHandler?.clearSingleViewRoomId();
        if (this.interactionHandler.isUserInSection(AppSections.POKER_SINGLE_ROOM_VIEW)) {
            this.connection?.joinLobby();
            this.interactionHandler?.setSection(AppSections.LOBBY_AOF_FIAT);
        }
    }

    private handleRoomLeaveFailed = (data: any) => {
        console.error(`Failed to leave room:`, data);
    }

    private handleRoomJoinFailed = (data: any) => {
        console.error(`Failed to join room:`, data);
        const roomId = data?.roomId;
        if (typeof roomId === 'string' && roomId) {
            reportDetachedIssue('join_failed', { roomId, data }, 'error');
            // If we pre-opened a blank detached window on user click, close it on join failures.
            releaseReservedWindow(roomId);
            if (isPopupDebugEnabled()) {
                console.warn('[detached] Released reserved window due to joinFailed', { roomId });
            }
        }
    }

    private handleRoomActivityMessage = (data: any) => {
        this.interactionHandler.addRoomActivityMessage(data);
    }

    private handleAppMessage = (event: string, data: any) => {
        if (event === 'userConnectedCount') {
            this.interactionHandler.setUserConnectedCount(data.count);
        }
        if (event === 'message' || event === 'serverMessage') {
            this.interactionHandler.handleServerMessage(data);
        }
    }

    private handleSitFailed = (data: any) => {
        switch (data.reason) {
            case 'alreadySeated':
                alert(t('Notifications:seat.alreadySeated'));
                break;
            case 'invalidSit':
                alert(t('Notifications:seat.invalidSit'));
                break;
            case 'noSeatsAvailable':
                alert(t('Notifications:seat.noSeatsAvailable'));
                break;
            default:
                alert(t('Notifications:seat.failed'));
        }
    };

    private handleBalanceOptionsResponse = (data: any) => {
        const { roomId, currentBalance, requiredTopUp, fixedBuyIn, limits } = data;

        this.interactionHandler.handleBalanceOptionsResponse(
            roomId,
            {
                currentBalance,
                requiredTopUp,
                fixedBuyIn,
                buyInLimits: limits,
                limits
            }
        );
    };

    private handleGameStarting = (data: any) => {
        const roomId = typeof data?.roomId === 'string' ? data.roomId : null;
        const seconds = typeof data?.seconds === 'number' && Number.isFinite(data.seconds)
            ? Math.ceil(data.seconds)
            : null;

        if (!roomId || seconds === null) return;

        const existingTimer = this.gameStartingClearTimers.get(roomId);
        if (existingTimer) {
            clearTimeout(existingTimer);
            this.gameStartingClearTimers.delete(roomId);
        }

        if (seconds <= 0) {
            this.interactionHandler.updateRoomState({
                roomId,
                state: { gameStartingIn: null }
            });
            return;
        }

        this.interactionHandler.updateRoomState({
            roomId,
            state: { gameStartingIn: seconds }
        });

        const clearTimer = setTimeout(() => {
            this.gameStartingClearTimers.delete(roomId);
            this.interactionHandler.updateRoomState({
                roomId,
                state: { gameStartingIn: null }
            });
        }, seconds * 1000);

        this.gameStartingClearTimers.set(roomId, clearTimer);
    };

    private handleTournamentMessage = (event: string, data: any) => {
        switch (event) {
            case 'rebuyOffer':
                this.interactionHandler.attachRebuyOfferToTournamentRoom(data);
                break;
            case 'addonOffer':
                this.interactionHandler.attachAddonOfferToTournamentRoom(data);
                break;
            case 'addonResult':
                this.interactionHandler.handleTournamentAddonResult(data);
                break;
            case 'error':
                this.interactionHandler.handleTournamentError(data);
                break;
            case 'addPlayerSuccess':
                this.interactionHandler.requestLobbyUpdate();
                void invalidateTournamentScheduleQueries();
                break;
            case 'removePlayerSuccess':
                this.interactionHandler.requestLobbyUpdate();
                void invalidateTournamentScheduleQueries();
                break;
            case 'playerUnregistered':
                this.interactionHandler.handleTournamentPlayerUnregistered(data);
                void invalidateTournamentScheduleQueries();
                break;
            case 'tournamentStarted':
                this.interactionHandler.notifyTournamentStarted(data);
                this.interactionHandler.requestLobbyUpdate();
                void invalidateTournamentScheduleQueries();
                break;
            case 'tournamentEnded':
                this.interactionHandler.notifyTournamentEnded(data);
                this.interactionHandler.requestLobbyUpdate();
                void invalidateTournamentScheduleQueries();
                break;
            case 'configUpdated':
                this.interactionHandler.requestLobbyUpdate();
                break;
            default:
                //console.warn(`Unhandled tournament event: ${event}`, data);
        }
    }
}
